import React from 'react';
import { Box, Typography, Button, Paper, Grid, Card, CardActionArea } from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import DescriptionIcon from '@mui/icons-material/Description';
import PeopleIcon from '@mui/icons-material/People';
import HouseIcon from '@mui/icons-material/House';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import ExtensionIcon from '@mui/icons-material/Extension';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { UserProfile } from '../types';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import { COLOR_PRIMARY_1 } from 'src/utils/colors';

interface QuestionTwoProps {
  userType: UserProfile['userType'];
  value: {
    strategy?: UserProfile['strategy'];
    role?: UserProfile['role'];
  };
  onChange: (data: {
    strategy?: UserProfile['strategy'];
    role?: UserProfile['role'];
    customRole?: string;
  }) => void;
  onNext: () => void;
  onBack: () => void;
  pageStyle: any;
}

const newPropertyInvestorStrategyOptions = [
  {
    value: 'buy',
    label: 'Buy a property',
    icon: <HouseIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'rent',
    label: 'Rent a property (rental arbitrage)',
    icon: <DescriptionIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'manage',
    label: 'Manage properties for others',
    icon: <PeopleIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'mix',
    label: 'Not sure yet',
    icon: <QuestionMarkIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
];

const propertyInvestorStrategyOptions = [
  {
    value: 'buy',
    label: 'I own the properties',
    icon: <HouseIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'rent',
    label: 'I rent the properties (rental arbitrage)',
    icon: <DescriptionIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'manage',
    label: 'I manage properties for others',
    icon: <PeopleIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'mix',
    label: 'A mix of the above',
    icon: <ExtensionIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
];

const roleOptions = [
  {
    value: 'consultant',
    label: "I'm a consultant or coach",
    icon: <PersonIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'property_management_company',
    label: 'I work for or run a property management company',
    icon: <BusinessIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'realtor',
    label: "I'm a realtor or real estate agent",
    icon: <HouseIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
  {
    value: 'unclassified',
    label: 'Other (Please describe)',
    icon: <EditNoteIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  },
];

const QuestionTwo: React.FC<QuestionTwoProps> = ({
  userType,
  value,
  onChange,
  onNext,
  onBack,
  pageStyle,
}) => {
  const radioRefs = React.useRef<(HTMLInputElement | null)[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [otherText, setOtherText] = React.useState('');
  const otherInputRef = React.useRef<HTMLDivElement | null>(null);

  const handleStrategySelect = (selected: UserProfile['strategy']) => {
    setError(null);
    onChange({ ...value, strategy: selected });
  };
  const handleRoleSelect = (selected: UserProfile['role']) => {
    setError(null);
    if (selected !== 'unclassified') {
      setOtherText('');
      onChange({ ...value, role: selected });
    } else {
      onChange({ ...value, role: 'unclassified' });
    }
  };
  const handleNext = () => {
    if (userType === 'property_manager') {
      if (!value.role) {
        setError('Please select an option to continue.');
        return;
      }
      if (value.role === 'unclassified' && !otherText.trim()) {
        setError('Please describe.');
        return;
      }
    } else {
      if (!value.strategy) {
        setError('Please select an option to continue.');
        return;
      }
    }

    if (value.role === 'unclassified' && otherText.trim()) {
      onChange({ ...value, role: 'unclassified', customRole: otherText });
    }

    setError(null);
    onNext();
  };

  // Keyboard navigation handler
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    idx: number,
    optionsArr: any[]
  ) => {
    if (['ArrowRight', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIdx = (idx + 1) % optionsArr.length;
      if (radioRefs.current[nextIdx]) {
        radioRefs.current[nextIdx]!.focus();
      }
    } else if (['ArrowLeft', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const prevIdx = (idx - 1 + optionsArr.length) % optionsArr.length;
      if (radioRefs.current[prevIdx]) {
        radioRefs.current[prevIdx]!.focus();
      }
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (userType === 'property_manager') {
        handleRoleSelect(optionsArr[idx].value as UserProfile['role']);
      } else {
        handleStrategySelect(optionsArr[idx].value as UserProfile['strategy']);
      }
    }
  };

  let options: { value: string; label: string; icon: React.ReactNode }[] = [];
  let question = '';
  let selectedValue = '';
  let onSelect: (val: string) => void = () => {};
  let stepDot = 2;

  switch (userType) {
    case 'new_property_investor':
      options = newPropertyInvestorStrategyOptions;
      question = 'How are you planning to start?';
      selectedValue = value.strategy || '';
      onSelect = (val: string) => handleStrategySelect(val as UserProfile['strategy']);
      stepDot = 2;
      break;
    case 'property_investor':
      options = propertyInvestorStrategyOptions;
      question = 'How do you operate your Airbnb listings?';
      selectedValue = value.strategy || '';
      onSelect = (val: string) => handleStrategySelect(val as UserProfile['strategy']);
      stepDot = 2;
      break;
    case 'property_manager':
      options = roleOptions;
      question = 'What best describes your role?';
      selectedValue = value.role || '';
      onSelect = (val: string) => handleRoleSelect(val as UserProfile['role']);
      stepDot = 2;
      break;
    default:
      break;
  }

  React.useEffect(() => {
    if (
      userType === 'property_manager' &&
      selectedValue === 'unclassified' &&
      otherInputRef.current
    ) {
      otherInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [userType, selectedValue]);

  return (
    <Paper
      elevation={3}
      sx={{
        ...pageStyle,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '@media (max-width:480px)': {
            maxHeight: 'calc(100vh - 120px)',
          },
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mt: { xs: 4, sm: 0 } }}>
          {question}
        </Typography>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box role="radiogroup" aria-label={question} sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {options.map((option, idx) => (
              <Grid item xs={12} sm={6} key={option.value}>
                <label style={{ width: '100%', display: 'block', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="questionTwoOption"
                    value={option.value}
                    checked={selectedValue === option.value}
                    onChange={() => onSelect(option.value)}
                    ref={el => (radioRefs.current[idx] = el)}
                    tabIndex={selectedValue === option.value ? 0 : -1}
                    aria-checked={selectedValue === option.value}
                    role="radio"
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                    onKeyDown={e => handleKeyDown(e, idx, options)}
                  />
                  <Card
                    variant={selectedValue === option.value ? 'elevation' : 'outlined'}
                    tabIndex={-1}
                    sx={{
                      border:
                        selectedValue === option.value
                          ? `2px solid ${COLOR_PRIMARY_1}`
                          : '1px solid #e0e0e0',
                      boxShadow: selectedValue === option.value ? 6 : 0,
                      background: selectedValue === option.value ? '#f0f7ff' : '#fff',
                      transition: '0.2s',
                      minHeight: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 3,
                      px: 2,
                      position: 'relative',
                      '&:focus-within': {
                        boxShadow: 8,
                      },
                      '@media (max-width:480px)': {
                        minHeight: 0,
                        py: 3,
                      },
                    }}
                    onClick={() => onSelect(option.value)}
                  >
                    <CardActionArea
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 0,
                        px: 0,
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        {option.icon}
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mt: 1,
                            fontWeight: 500,
                            maxWidth: '30ch',
                            wordBreak: 'break-word',
                            textAlign: 'center',
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Card>
                </label>
                {/* Show text field if 'Other' is selected for property_manager */}
                {userType === 'property_manager' &&
                  option.value === 'unclassified' &&
                  selectedValue === 'unclassified' && (
                    <Box sx={{ mt: 2 }} ref={otherInputRef}>
                      <TextField
                        fullWidth
                        placeholder="Please Describe"
                        value={otherText}
                        onChange={e => {
                          const newValue = e.target.value || 'unclassified';
                          setOtherText(newValue);
                        }}
                        inputProps={{ maxLength: 80 }}
                      />
                    </Box>
                  )}
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          {/* Progress dots: step 2 of 3 */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e0e0e0' }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLOR_PRIMARY_1 }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e0e0e0' }} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', pb: { xs: 2, sm: 0 } }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={
            userType === 'property_manager'
              ? !selectedValue || (selectedValue === 'unclassified' && !otherText.trim())
              : !selectedValue
          }
        >
          Next
        </Button>
      </Box>
    </Paper>
  );
};

export default QuestionTwo;
